import React, { useState, useEffect, Fragment, useRef } from "react";
import { Radio, Row, Col, Popover } from "antd";
import {
	ProductPricingPlan,
	DistributorBranchDetails,
	Distributor,
	distributorLabel,
	noOfDistributorsIntegrated,
	displayPricingName
} from "@/Contracts/Products";
import { RadioChangeEvent } from "antd/lib/radio";
import EditIcon from "config/assets/DocumentsPage/edit-dark.svg";
import { Form } from "@ant-design/compatible";
import { useSelector } from "react-redux";
import { FormAlignment } from "@/Contracts";

import "./ProductPricingOptions.less";
import { currencyFormatter } from "@/Utils/Currency";
import { IHandlePricingIdChangeProps } from "../../DocumentProductConfigureDrawerForm/DocumentProductConfigureDrawerForm";
import { GlobalState } from "@/Reducers/interface";
import { ZRadioGroup } from "@atoms";

interface PricingProps {
	pricing: ProductPricingPlan[] | null;
	selectedPricingId?: string | null;
	selectedWarehouse?: string | null;
	handlePricingIdChange: (e: IHandlePricingIdChangeProps) => void;
	handleWarehouseChange?: (newWarehouse: string | undefined) => void;
	hideWarehouseOptions?: boolean;
	formAlignment?: FormAlignment;
	isReadOnly?: boolean;
}

export const ProductPricingOptions: React.FC<PricingProps> = ({
	pricing,
	selectedPricingId,
	selectedWarehouse,
	handlePricingIdChange,
	handleWarehouseChange,
	hideWarehouseOptions,
	formAlignment,
	isReadOnly = false
}) => {
	const integrations = useSelector(
		(state: GlobalState) => state.user.userCompany.integrations
	);

	const [pricingVsWarehouseArr, setPricingVsWarehouseArr] = useState<
		{ pricing: string | null; warehouse: string | undefined }[]
	>([]);

	const [selectedProductPricingOptionId, setSelectedProductPricingOptionId] =
		useState<string>(selectedPricingId ?? "");
	useEffect(() => {
		setSelectedProductPricingOptionId(selectedPricingId ?? "");
	}, [selectedPricingId]);

	const onWarehouseChange = (
		currentPricing: ProductPricingPlan,
		newWarehouse: string | undefined
	) => {
		setPricingVsWarehouseArr(
			[...pricingVsWarehouseArr]
				.filter(element => element.pricing !== currentPricing.id)
				.concat({ pricing: currentPricing.id, warehouse: newWarehouse })
		);
		if (currentPricing.id === selectedPricingId) {
			handleWarehouseChange?.(newWarehouse);
		}
	};

	const onPricingIdChange = (e: RadioChangeEvent) => {
		setSelectedProductPricingOptionId(e.target.value);
		const selectedPricing = pricing?.find(
			pricing => pricing.id === e.target.value
		);
		handleWarehouseChange?.(
			pricingVsWarehouseArr.find(
				element => element.pricing === selectedPricing?.id
			)?.warehouse
		);
		handlePricingIdChange({ value: null, radioChangeEvent: e });
	};

	useEffect(() => {
		const pricingVsWarehouse = pricing?.map(pricing => {
			const warehouse =
				pricing.distributor_pricing_details?.branch_wise_details.find(
					branch => branch.branch_name === selectedWarehouse
				)?.branch_name ||
				pricing.distributor_pricing_details?.branch_wise_details[0]
					?.branch_name;

			return { pricing: pricing.id, warehouse: warehouse };
		});

		setPricingVsWarehouseArr(pricingVsWarehouse ? pricingVsWarehouse : []);
	}, [pricing, selectedWarehouse]);

	const catalogPricing =
		pricing?.filter(pricing => !!!pricing.distributor_pricing_details) ||
		[];

	const distributorPricing =
		pricing?.filter(pricing => !!pricing.distributor_pricing_details) || [];

	return (
		<Form.Item {...formAlignment} label="Pricing" labelAlign="left">
			<ZRadioGroup
				className="product-image-radio-container"
				onChange={onPricingIdChange}
				value={selectedProductPricingOptionId}
				disabled={isReadOnly}
			>
				{catalogPricing.length > 0 && (
					<Col span={24} className="text-1 pb-4">
						My catalog
					</Col>
				)}
				{catalogPricing.map((pricing, index) => {
					return (
						<PricingRadio
							pricing={pricing}
							key={index}
							isReadOnly={isReadOnly}
						/>
					);
				})}
				{noOfDistributorsIntegrated(integrations) > 0 && (
					<Col span={24} className="text-1 py-4">
						Realtime pricing
					</Col>
				)}
				{distributorPricing.map((pricing, index) => {
					const warehouse = pricingVsWarehouseArr.find(
						element => element.pricing === pricing.id
					)?.warehouse;
					return (
						<DistributorPricingRadio
							pricing={pricing}
							key={index}
							selectedWarehouse={warehouse}
							onWarehouseChange={onWarehouseChange}
							hideWarehouseOptions={hideWarehouseOptions}
							isReadOnly={isReadOnly}
						/>
					);
				})}
				<ShowDistributorNotAvailableRadio pricing={pricing} />
				<Col span={24} className="text-1 py-4">
					Create new
				</Col>
				<Radio
					className="product-image-radio"
					checked={!selectedPricingId}
					value={""}
					data-testid="test-pricing-id-custom"
					disabled={isReadOnly}
				>
					Custom
				</Radio>
			</ZRadioGroup>
		</Form.Item>
	);
};

interface PricingRadioProps {
	pricing: ProductPricingPlan;
	isReadOnly?: boolean;
}

interface DistributorPricingRadioProps extends PricingRadioProps {
	selectedWarehouse: string | undefined;
	onWarehouseChange: (
		currentPricing: ProductPricingPlan,
		newWarehouse: string
	) => void;
	hideWarehouseOptions?: boolean;
	isReadOnly?: boolean;
}

const DistributorPricingRadio = ({
	pricing,
	selectedWarehouse,
	onWarehouseChange,
	hideWarehouseOptions,
	isReadOnly = false
}: DistributorPricingRadioProps) => {
	const stockLevel = pricing.distributor_pricing_details
		? `${pricing.distributor_pricing_details.total_availability}/${pricing.distributor_pricing_details.total_on_order}`
		: "NA";

	return (
		<Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
			<Radio
				value={pricing.id}
				className="pricing-list-radio"
				data-testid={`test-pricing-id-${pricing.id}`}
				disabled={isReadOnly}
			>
				<span className="distributor-pricing-area">
					<span className="distributor-name">
						{`${displayPricingName(pricing)}: ${
							pricing.cost_price !== null
								? `${currencyFormatter(pricing.cost_price)}`
								: "NA"
						}`}
					</span>
					{pricing.distributor_pricing_details?.branch_wise_details &&
						!hideWarehouseOptions && (
							<WarehousePopover
								pricing={pricing}
								selectedWarehouse={selectedWarehouse}
								onWarehouseChange={onWarehouseChange}
								isReadOnly={isReadOnly}
							/>
						)}
					<br />
					<span className="distributor-stock">{`Stock Level: ${stockLevel}`}</span>
				</span>
			</Radio>
		</Col>
	);
};

const PricingRadio = ({ pricing, isReadOnly = false }: PricingRadioProps) => {
	return (
		<Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
			<Radio value={pricing.id} className="mb-2" disabled={isReadOnly}>
				{(pricing.name && !!pricing.name.length && pricing.name) ||
					"Default Pricing"}
			</Radio>
		</Col>
	);
};

interface IWarehousePopover {
	readonly pricing: ProductPricingPlan;
	readonly selectedWarehouse: string | undefined;
	readonly onWarehouseChange: (
		currentPricing: ProductPricingPlan,
		newWarehouse: string
	) => void;
	readonly isReadOnly?: boolean;
}

export const WarehousePopover: React.FC<IWarehousePopover> = props => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [warehouse, setWarehouse] = useState<
		DistributorBranchDetails | undefined
	>(undefined);
	const [visible, setVisible] = useState(false);

	const onChange = (e: RadioChangeEvent) => {
		setVisible(false);
		setWarehouse(
			props.pricing.distributor_pricing_details?.branch_wise_details.find(
				branch => branch.branch_name === e.target.value
			)
		);
		props.onWarehouseChange(props.pricing, e.target.value);
	};

	useEffect(() => {
		const warehouseToSet =
			props.pricing.distributor_pricing_details?.branch_wise_details.find(
				branch => branch.branch_name === props.selectedWarehouse
			);

		if (warehouseToSet) {
			setWarehouse(warehouseToSet);
		} else {
			setWarehouse(
				props.pricing.distributor_pricing_details
					?.branch_wise_details[0]
			);
		}
	}, [props.pricing, props.selectedWarehouse]);

	return (props.pricing?.distributor_pricing_details?.branch_wise_details
		?.length || 0) > 0 ? (
		<div
			ref={containerRef}
			data-testid={`warehouse-${props.pricing.id}`}
			className="warehouse-popover-menu"
		>
			<Popover
				content={
					<ZRadioGroup
						className="product-image-radio-container"
						value={warehouse?.branch_name}
						onChange={onChange}
						disabled={props.isReadOnly}
					>
						<Row>
							{props.pricing.distributor_pricing_details?.branch_wise_details.map(
								(branch, index) => {
									return (
										<Col
											key={index}
											xs={24}
											sm={24}
											md={24}
											lg={24}
											xl={24}
											xxl={24}
										>
											<Radio
												value={branch.branch_name}
												className="pricing-list-radio"
											>
												{`${branch.branch_name} (${branch.availability}/${branch.on_order})`}
											</Radio>
										</Col>
									);
								}
							)}
						</Row>
					</ZRadioGroup>
				}
				trigger="click"
				visible={visible}
				placement="bottomLeft"
				onVisibleChange={setVisible}
				getPopupContainer={trigger =>
					containerRef.current
						? (containerRef.current as HTMLElement)
						: (trigger as HTMLElement)
				}
				overlayClassName="warehouse-popover-content"
			>
				<span className="distributor-warehouse">
					{`${warehouse?.branch_name} (${warehouse?.availability}/${warehouse?.on_order})`}
					<EditIcon />
				</span>
			</Popover>
		</div>
	) : (
		<Fragment />
	);
};

export const ShowDistributorNotAvailableRadio = (props: {
	pricing: ProductPricingPlan[] | null;
}) => {
	const integrations = useSelector(
		(state: GlobalState) => state.user.userCompany.integrations
	);
	const isIngramIntegrated = integrations.ingram !== null;
	const isTechdataIntegrated = integrations.tech_data !== null;
	const isDAndHIntegrated = integrations.d_and_h !== null;
	const isSynnexIntegrated = integrations.synnex !== null;

	const showIngram =
		isIngramIntegrated &&
		!!!props.pricing?.find(
			pricing =>
				pricing.distributor_pricing_details?.distributor ===
				Distributor.Ingram
		);

	const showTechData =
		isTechdataIntegrated &&
		!!!props.pricing?.find(
			pricing =>
				pricing.distributor_pricing_details?.distributor ===
				Distributor.TechData
		);

	const showDAndH =
		isDAndHIntegrated &&
		!!!props.pricing?.find(
			pricing =>
				pricing.distributor_pricing_details?.distributor ===
				Distributor.DAndH
		);

	const showSynnex =
		isSynnexIntegrated &&
		!!!props.pricing?.find(
			pricing =>
				pricing.distributor_pricing_details?.distributor ===
				Distributor.Synnex
		);

	const radio = (distributor: Distributor) => {
		return (
			<Col key={distributor.toString()} span={24}>
				<Radio value={"ingram"} className="pricing-list-radio" disabled>
					<span className="distributor-pricing-area">
						<span className="distributor-name">{`${distributorLabel[distributor]}: Not available`}</span>
						<br />
						<span className="distributor-stock">{`Stock Level: NA`}</span>
					</span>
				</Radio>
			</Col>
		);
	};

	return showIngram || showTechData || showDAndH || showSynnex ? (
		<>
			{showIngram && radio(Distributor.Ingram)}
			{showTechData && radio(Distributor.TechData)}
			{showDAndH && radio(Distributor.DAndH)}
			{showSynnex && radio(Distributor.Synnex)}
		</>
	) : (
		<Fragment />
	);
};

export default ProductPricingOptions;
